import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Eye, Download, ArrowUpDown, Trash2, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '../utils/currency';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generatePaymentPlannerPDF } from '../utils/paymentPlannerPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  // Certificate Payment state
  const [certStep, setCertStep] = useState(0);
  const [certSelectedProjects, setCertSelectedProjects] = useState([]);
  const [certPreviewData, setCertPreviewData] = useState([]);

  // Pending Tasks state
  const [showPendingTasks, setShowPendingTasks] = useState(false);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [sortField, setSortField] = useState('deadline');
  const [sortDir, setSortDir] = useState('asc');

  // Payment Planner state
  const [showPaymentPlanner, setShowPaymentPlanner] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState([]);

  useEffect(() => { fetchMemberDetails(); }, [id]);

  const fetchMemberDetails = async () => {
    try {
      const [memberRes, txnRes, projRes] = await Promise.all([
        axios.get(`${API}/team/${id}`),
        axios.get(`${API}/team/${id}/transactions`),
        axios.get(`${API}/projects`)
      ]);
      setMember(memberRes.data);
      setTransactions(txnRes.data);
      setAllProjects(projRes.data);
      setLoading(false);
    } catch {
      toast.error('Failed to load member details');
      setLoading(false);
    }
  };

  const openContractDetail = (project) => { setSelectedContract(project); setContractDialogOpen(true); };

  const viewContractFile = async (projectId, memberName) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/team-contract/${encodeURIComponent(memberName)}`);
      if (res.data.contractFile) {
        const win = window.open('', '_blank');
        if (res.data.contractFile.startsWith('data:application/pdf')) {
          win.document.write(`<iframe src="${res.data.contractFile}" style="width:100%;height:100%;border:none;"></iframe>`);
        } else {
          win.document.write(`<img src="${res.data.contractFile}" style="max-width:100%;height:auto;" />`);
        }
      } else { toast.error('No contract file uploaded'); }
    } catch { toast.error('Failed to load contract file'); }
  };

  const downloadContractFile = async (projectId, memberName) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/team-contract/${encodeURIComponent(memberName)}`);
      if (res.data.contractFile) {
        const link = document.createElement('a'); link.href = res.data.contractFile;
        link.download = res.data.contractFileName || 'contract'; link.click();
      } else { toast.error('No contract file uploaded'); }
    } catch { toast.error('Failed to download'); }
  };

  // Get sub-work names for a member on a project
  const getAssignedSubWorks = (projectId) => {
    const fullProject = allProjects.find(p => p.id === projectId);
    if (!fullProject) return [];
    const tm = fullProject.teamMembers?.find(m => m.name === member?.name);
    if (!tm || !tm.subWorkIds?.length) return [];
    return fullProject.subWorks?.filter(sw => tm.subWorkIds.includes(sw.id)) || [];
  };

  // Certificate Payment flow
  const startCertificate = () => { setCertStep(1); setCertSelectedProjects([]); setCertPreviewData([]); };

  const toggleCertProject = (pid) => {
    setCertSelectedProjects(prev => prev.includes(pid) ? prev.filter(i => i !== pid) : [...prev, pid]);
  };

  const generateCertPreview = () => {
    if (certSelectedProjects.length === 0) { toast.error('Select at least one project'); return; }
    const data = certSelectedProjects.map(pid => {
      const proj = member.projects.find(p => p.projectId === pid);
      if (!proj) return null;
      const subWorks = getAssignedSubWorks(pid);
      const projTxns = transactions.filter(t => t.projectId === pid);
      return { ...proj, subWorks, transactions: projTxns };
    }).filter(Boolean);
    setCertPreviewData(data);
    setCertStep(2);
  };

  const generateCertPDF = () => {
    if (!member || certPreviewData.length === 0) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    let y = 20;
    const now = new Date();
    const dateStr = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
    const fmtRp = (v) => `Rp ${(v || 0).toLocaleString('id-ID')}`;

    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('REKAPITULASI PEMBAYARAN TEAM', pw / 2, y, { align: 'center' }); y += 12;
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('UPDATE', 14, y); doc.text(`: ${dateStr}`, 60, y); y += 6;
    doc.text('TEAM MEMBER', 14, y); doc.text(`: ${member.name.toUpperCase()}`, 60, y); y += 6;
    doc.text('TAHUN PROJECT', 14, y); doc.text(`: ${now.getFullYear()}`, 60, y); y += 10;

    let grandContract = 0, grandPaid = 0;

    certPreviewData.forEach(proj => {
      if (y > 230) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
      doc.text('PROJECT', 14, y); doc.text(`: ${proj.projectName}`, 60, y); y += 6;
      doc.setFont('helvetica', 'normal');
      doc.text('TANGGAL PENUGASAN', 14, y); doc.text(`: ${proj.assignmentDate || '-'}`, 60, y); y += 6;
      doc.text('TOTAL FEE PEKERJAAN', 14, y); doc.text(`: ${fmtRp(proj.contractValue)}`, 60, y); y += 4;

      // Sub-works
      if (proj.subWorks.length > 0) {
        doc.setFontSize(9); doc.setFont('helvetica', 'italic');
        doc.text('Scope:', 14, y + 4);
        proj.subWorks.forEach((sw, i) => {
          doc.text(`  - ${sw.name}`, 30, y + 4 + (i + 1) * 4);
        });
        y += 4 + (proj.subWorks.length + 1) * 4;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      } else { y += 4; }

      const rows = proj.transactions.map((t, i) => [`PAYMENT ${i + 1}`, t.date || '-', fmtRp(t.amount)]);
      const tp = proj.totalPaid || 0;
      const rem = (proj.contractValue || 0) - tp;
      grandContract += proj.contractValue || 0; grandPaid += tp;
      rows.push([{ content: 'TOTAL DIBAYARKAN', styles: { fontStyle: 'bold' } }, '', { content: fmtRp(tp), styles: { fontStyle: 'bold' } }]);
      rows.push([{ content: 'SISA PEMBAYARAN', styles: { fontStyle: 'bold' } }, '', { content: fmtRp(rem), styles: { fontStyle: 'bold', textColor: rem > 0 ? [220, 50, 50] : [0, 0, 0] } }]);
      if (rows.length === 2) rows.unshift(['PAYMENT 1', '-', '-']);

      autoTable(doc, { startY: y, head: [['TERMIN PEMBAYARAN', 'TANGGAL', 'JUMLAH DI BAYAR']], body: rows, theme: 'grid', styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] }, margin: { left: 14, right: 14 } });
      y = doc.lastAutoTable.finalY + 12;
    });

    if (y > 240) { doc.addPage(); y = 20; }
    doc.setDrawColor(0); doc.line(14, y, pw - 14, y); y += 8;
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text('Rekapitulasi :', 14, y); y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text('Total Fee Pekerjaan', 14, y); doc.text(`: ${fmtRp(grandContract)}`, 70, y); y += 6;
    doc.text('Total Dibayarkan', 14, y); doc.text(`: ${fmtRp(grandPaid)}`, 70, y); y += 6;
    doc.text('Total Sisa Pembayaran', 14, y); doc.text(`: ${fmtRp(grandContract - grandPaid)}`, 70, y);

    doc.save(`Certificate_Payment_${member.name.replace(/\s/g, '_')}_${dateStr.replace(/\//g, '-')}.pdf`);
    toast.success('Certificate Payment PDF generated!');
  };

  // Pending Tasks
  const fetchPendingTasks = async () => {
    try {
      const res = await axios.get(`${API}/team/${id}/pending-tasks`);
      setPendingTasks(res.data);
      setShowPendingTasks(true);
      setSortField('deadline');
      setSortDir('asc');
    } catch { toast.error('Failed to load pending tasks'); }
  };

  // Mark a single task as completed for THIS team member (toggles their checkbox in the task)
  const markTaskCompleted = async (taskId) => {
    if (!member) return;
    try {
      await axios.put(`${API}/tasks/${taskId}/toggle-person/${encodeURIComponent(member.name)}`);
      // Refresh the pending-task list — finished tasks drop off because the endpoint
      // filters by not completed.
      const res = await axios.get(`${API}/team/${id}/pending-tasks`);
      setPendingTasks(res.data);
      toast.success('Task marked as completed!');
    } catch {
      toast.error('Failed to mark task as completed');
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedPendingTasks = [...pendingTasks].sort((a, b) => {
    let va, vb;
    switch (sortField) {
      case 'deadline':
        va = a.personDeadline || a.mainDeadline || '9999';
        vb = b.personDeadline || b.mainDeadline || '9999';
        break;
      case 'project': va = a.projectName; vb = b.projectName; break;
      case 'task': va = a.taskDescription; vb = b.taskDescription; break;
      case 'status': va = (a.waitingFor?.length || 0).toString(); vb = (b.waitingFor?.length || 0).toString(); break;
      default: va = ''; vb = '';
    }
    return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });

  // Get unique months that have deadlines for the calendar view
  const getDeadlineMonths = () => {
    const months = new Map();
    pendingTasks.forEach(t => {
      const d = t.personDeadline || t.mainDeadline;
      if (!d) return;
      const date = new Date(d);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      if (!months.has(key)) months.set(key, { year: date.getFullYear(), month: date.getMonth() });
    });
    return Array.from(months.values()).sort((a, b) => a.year - b.year || a.month - b.month);
  };

  const getDeadlineDatesForMonth = (year, month) => {
    return pendingTasks
      .filter(t => {
        const d = t.personDeadline || t.mainDeadline;
        if (!d) return false;
        const date = new Date(d);
        return date.getFullYear() === year && date.getMonth() === month;
      })
      .map(t => ({
        date: new Date(t.personDeadline || t.mainDeadline),
        project: t.projectName,
        task: t.personSubDescription || t.taskDescription
      }));
  };

  const SortHeader = ({ field, children }) => (
    <TableHead className="cursor-pointer select-none hover:bg-slate-100" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-blue-600' : 'text-slate-300'}`} />
      </div>
    </TableHead>
  );

  const generatePendingTasksPDF = () => {
    if (!member || pendingTasks.length === 0) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    let y = 20;
    const now = new Date();
    const dateStr = `${now.getDate().toString().padStart(2,'0')}/${(now.getMonth()+1).toString().padStart(2,'0')}/${now.getFullYear()}`;

    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('PENDING TASKS REPORT', pw / 2, y, { align: 'center' }); y += 10;
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('DATE', 14, y); doc.text(`: ${dateStr}`, 55, y); y += 6;
    doc.text('TEAM MEMBER', 14, y); doc.text(`: ${member.name.toUpperCase()}`, 55, y); y += 10;

    const head = [['No', 'Project', 'Task', 'Job Detail', 'Deadline', 'Status']];
    const body = pendingTasks.map((t, i) => [
      i + 1,
      t.projectName,
      t.taskDescription,
      t.personSubDescription || '-',
      t.personDeadline || t.mainDeadline || '-',
      t.waitingFor?.length > 0 ? `Waiting: ${t.waitingFor.join(', ')}` : 'Ready'
    ]);

    autoTable(doc, {
      startY: y, head, body, theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
      columnStyles: { 0: { cellWidth: 12 }, 1: { cellWidth: 40 } },
      margin: { left: 14, right: 14 },
    });

    doc.save(`Pending_Tasks_${member.name.replace(/\s/g, '_')}_${dateStr.replace(/\//g, '-')}.pdf`);
    toast.success('Pending Tasks PDF generated!');
  };

  // Payment Planner
  const openPaymentPlanner = () => {
    if (!member?.projects?.length) { toast.error('No projects found'); return; }
    const plan = member.projects.map(proj => {
      const fullProject = allProjects.find(p => p.id === proj.projectId);
      const clientPaidPercent = fullProject && fullProject.contractValue > 0
        ? Math.round(((fullProject.initialPaid || 0) + (fullProject.amountPaid || 0)) / fullProject.contractValue * 100)
        : 0;
      return {
        projectId: proj.projectId,
        projectName: proj.projectName,
        // Inherit the project's finance book so the planner lands in the correct book
        financeBook: (fullProject?.financeBook) || 'freelance',
        contractValue: proj.contractValue || 0,
        alreadyPaid: proj.totalPaid || 0,
        alreadyPaidPercent: proj.contractValue > 0 ? Math.round((proj.totalPaid || 0) / proj.contractValue * 100) : 0,
        clientPaidPercent,
        myPaymentPercent: 0,
        amountToPay: 0,
      };
    });
    setPaymentPlan(plan);
    setShowPaymentPlanner(true);
  };

  const updatePaymentPercent = (index, percent) => {
    const updated = [...paymentPlan];
    const p = updated[index];
    p.myPaymentPercent = parseFloat(percent) || 0;
    // Additional payment: percentage is what you pay NOW, not cumulative
    p.amountToPay = Math.round(p.contractValue * p.myPaymentPercent / 100);
    setPaymentPlan(updated);
  };

  const generatePaymentPlanPDF = () => {
    const plan = paymentPlan.map(p => ({
      projectName: p.projectName,
      financeBook: p.financeBook,
      contractValue: p.contractValue,
      alreadyPaid: p.alreadyPaid,
      alreadyPaidPercent: p.alreadyPaidPercent,
      myPaymentPercent: p.myPaymentPercent,
      amountToPay: p.amountToPay,
    }));
    generatePaymentPlannerPDF({ memberName: member.name, plan });
    toast.success('Payment Planner PDF generated!');
  };

  // Postponed Payments state
  const [postponedPayments, setPostponedPayments] = useState([]);

  const fetchPostponedPayments = async () => {
    try {
      const res = await axios.get(`${API}/postponed-payments?team_member_id=${id}`);
      setPostponedPayments(res.data);
    } catch {}
  };

  useEffect(() => { if (id) fetchPostponedPayments(); }, [id]);

  const saveAsPostponedPayment = async () => {
    const activePlans = paymentPlan.filter(p => p.myPaymentPercent > 0);
    if (activePlans.length === 0) { toast.error('Set at least one payment %'); return; }

    // Compute unique books covered by this planner. Multi-book planners are OK —
    // on execute they will create one transaction per book automatically.
    const books = Array.from(new Set(activePlans.map(p => p.financeBook || 'freelance')));
    const primaryBook = books[0];  // for legacy `financeBook` field

    try {
      await axios.post(`${API}/postponed-payments`, {
        teamMemberId: id,
        teamMemberName: member.name,
        financeBook: primaryBook,          // legacy single-book compat
        financeBooks: books,               // new multi-book field
        projects: activePlans.map(p => ({
          projectId: p.projectId, projectName: p.projectName,
          financeBook: p.financeBook || 'freelance',
          contractValue: p.contractValue, alreadyPaid: p.alreadyPaid,
          paymentPercent: p.myPaymentPercent, amount: p.amountToPay
        })),
        totalAmount: activePlans.reduce((s, p) => s + p.amountToPay, 0)
      });
      const label = books.length > 1
        ? `Saved as planned payment (mixed books: ${books.join(' + ')})`
        : `Saved as planned payment (${primaryBook === 'company' ? 'Company' : 'Freelance'} book)`;
      toast.success(label);
      setShowPaymentPlanner(false);
      fetchPostponedPayments();
    } catch { toast.error('Failed to save'); }
  };

  const deletePostponedPayment = async (ppId) => {
    try {
      await axios.delete(`${API}/postponed-payments/${ppId}`);
      toast.success('Deleted');
      fetchPostponedPayments();
    } catch { toast.error('Failed to delete'); }
  };

  const executePostponedPayment = (pp) => {
    // Navigate to finance with pre-filled data
    const params = new URLSearchParams({
      book: pp.financeBook || 'freelance',
      prefill: JSON.stringify({
        name: `Payment - ${pp.teamMemberName}`,
        type: 'expense',
        teamMemberId: pp.teamMemberId,
        amount: pp.totalAmount,
        postponedPaymentId: pp.id,
        projectAllocations: pp.projects.map(p => ({
          projectId: p.projectId, projectName: p.projectName,
          percent: p.paymentPercent, amount: p.amount
        }))
      })
    });
    navigate(`/finance?${params.toString()}`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Loading...</div></div>;
  if (!member) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Team member not found</div></div>;

  const totalContract = member.totalContractValue || 0;
  const totalPaid = member.totalPaid || 0;
  const totalUnpaid = member.totalUnpaid || 0;

  return (
    <div className="min-h-screen p-6" data-testid="team-detail-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 relative">
          <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded absolute top-0 right-0">A.2.{member?.name?.charAt(0) || 'x'}</span>
          <Button variant="ghost" onClick={() => navigate('/team')} className="mb-4" data-testid="back-to-team">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Team Overview
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Work Sans, sans-serif'}}>{member.name}</h1>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="text-base px-3 py-1">{member.role}</Badge>
                <span className="text-slate-600">{member.projects?.length || 0} active projects</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchPendingTasks} variant="outline" data-testid="pending-tasks-btn">
                <FileText className="w-4 h-4 mr-2" /> On Going Task
              </Button>
              <Button onClick={openPaymentPlanner} variant="outline" data-testid="payment-planner-btn">
                <FileText className="w-4 h-4 mr-2" /> Payment Planner
              </Button>
              {postponedPayments.length > 0 && (
                <Button onClick={() => setShowPendingTasks(false) || setShowPaymentPlanner(false)} variant="outline" className="border-amber-300 text-amber-700" data-testid="postponed-btn">
                  <FileText className="w-4 h-4 mr-2" /> Postponed ({postponedPayments.length})
                </Button>
              )}
              <Button onClick={startCertificate} className="bg-slate-800 hover:bg-slate-900" data-testid="generate-certificate-btn">
                <FileText className="w-4 h-4 mr-2" /> Certificate Payment
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white shadow-sm"><CardContent className="p-6"><p className="text-sm text-slate-600 mb-1">Total Contract</p><p className="text-2xl font-bold text-slate-800">{formatCurrency(totalContract)}</p></CardContent></Card>
          <Card className="bg-white shadow-sm"><CardContent className="p-6"><p className="text-sm text-slate-600 mb-1">Total Paid</p><p className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</p></CardContent></Card>
          <Card className="bg-white shadow-sm"><CardContent className="p-6"><p className="text-sm text-slate-600 mb-1">Total Unpaid</p><p className="text-2xl font-bold text-orange-600">{formatCurrency(totalUnpaid)}</p></CardContent></Card>
          <Card className="bg-blue-50 border-blue-200 shadow-sm"><CardContent className="p-6"><p className="text-sm text-blue-700 mb-1">Pending Tasks</p><p className="text-2xl font-bold text-blue-800">{member.totalPendingTasks || 0}</p></CardContent></Card>
        </div>

        {/* Pending Tasks Panel */}
        {showPendingTasks && (
          <Card className="mb-6 border-blue-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pending Tasks ({pendingTasks.length})</CardTitle>
                <div className="flex gap-2">
                  {pendingTasks.length > 0 && (
                    <Button size="sm" onClick={generatePendingTasksPDF} data-testid="export-pending-tasks">
                      <FileText className="w-4 h-4 mr-2" /> Export PDF
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setShowPendingTasks(false)}>Close</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {pendingTasks.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-4">No pending tasks!</p>
              ) : (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">No</TableHead>
                      <SortHeader field="project">Project</SortHeader>
                      <SortHeader field="task">Task</SortHeader>
                      <TableHead>Job Detail</TableHead>
                      <SortHeader field="deadline">Deadline</SortHeader>
                      <SortHeader field="status">Status</SortHeader>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedPendingTasks.map((task, i) => (
                      <TableRow key={task.taskId + i}>
                        <TableCell className="text-sm">{i + 1}</TableCell>
                        <TableCell className="text-sm font-medium">
                          <button
                            type="button"
                            onClick={() => navigate(`/project/${task.projectId}`)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
                            data-testid={`open-project-${task.taskId}`}
                          >
                            {task.projectName}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm">{task.taskDescription}</TableCell>
                        <TableCell className="text-sm text-slate-600">{task.personSubDescription || '-'}</TableCell>
                        <TableCell className="text-sm">
                          <span className={new Date(task.personDeadline || task.mainDeadline) < new Date() ? 'text-red-600 font-medium' : ''}>
                            {task.personDeadline || task.mainDeadline || '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {task.waitingFor && task.waitingFor.length > 0 ? (
                            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-medium">
                              Waiting for {task.waitingFor.join(', ')}
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                              Ready
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markTaskCompleted(task.taskId)}
                            disabled={task.waitingFor && task.waitingFor.length > 0}
                            className="h-8 border-green-500 text-green-700 hover:bg-green-50 hover:text-green-800 disabled:opacity-50"
                            data-testid={`mark-task-done-${task.taskId}`}
                            title={
                              task.waitingFor && task.waitingFor.length > 0
                                ? `Waiting for ${task.waitingFor.join(', ')} — complete those first`
                                : 'Mark as done'
                            }
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Done
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Monthly Calendar View */}
                <div className="mt-6 border-t pt-6">
                  <h3 className="text-sm font-medium text-slate-700 mb-4">Deadline Calendar</h3>
                  <div className="flex flex-wrap gap-6">
                    {getDeadlineMonths().map(({ year, month }) => {
                      const deadlines = getDeadlineDatesForMonth(year, month);
                      const monthName = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      const daysInMonth = new Date(year, month + 1, 0).getDate();
                      const firstDay = new Date(year, month, 1).getDay();
                      const today = new Date();

                      return (
                        <div key={`${year}-${month}`} className="bg-white border rounded-lg p-4 shadow-sm">
                          <h4 className="text-sm font-bold text-center mb-3">{monthName}</h4>
                          <div className="grid grid-cols-7 gap-1 text-center">
                            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                              <div key={d} className="text-[10px] text-slate-400 font-medium py-1">{d}</div>
                            ))}
                            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const dateObj = new Date(year, month, day);
                              const dayDeadlines = deadlines.filter(dl => dl.date.getDate() === day);
                              const hasDeadline = dayDeadlines.length > 0;
                              const isToday = dateObj.toDateString() === today.toDateString();
                              const isPast = dateObj < today && !isToday;

                              return (
                                <div
                                  key={day}
                                  className={`relative w-8 h-8 flex items-center justify-center rounded text-xs cursor-default
                                    ${hasDeadline ? (isPast ? 'bg-red-600 text-white font-bold' : 'bg-red-500 text-white font-bold') : ''}
                                    ${isToday && !hasDeadline ? 'bg-blue-100 text-blue-800 font-bold ring-1 ring-blue-400' : ''}
                                    ${!hasDeadline && !isToday ? 'text-slate-600' : ''}
                                  `}
                                  title={hasDeadline ? dayDeadlines.map(dl => `${dl.project}: ${dl.task}`).join('\n') : ''}
                                >
                                  {day}
                                  {hasDeadline && (
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-400 rounded-full" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payment Planner */}
        {showPaymentPlanner && (
          <Card className="mb-6 border-green-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payment Planner — {member.name}</CardTitle>
                <div className="flex gap-2">
                  {paymentPlan.some(p => p.myPaymentPercent > 0) && (
                    <>
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-700" onClick={saveAsPostponedPayment} data-testid="save-postponed">
                      Save as Planned
                    </Button>
                    <Button size="sm" onClick={generatePaymentPlanPDF} data-testid="export-payment-plan">
                      <FileText className="w-4 h-4 mr-2" /> Print PDF
                    </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setShowPaymentPlanner(false)}>Close</Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">Fill in "My Payment %" to calculate amount. "Client Paid %" is hidden from PDF print.</p>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">No</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead className="text-right">Kontrak</TableHead>
                    <TableHead className="text-right">Sudah Dibayar</TableHead>
                    <TableHead className="text-center bg-amber-50">Client Paid %</TableHead>
                    <TableHead className="text-center">Pembayaran Saat Ini (%)</TableHead>
                    <TableHead className="text-right">Jumlah Dibayarkan</TableHead>
                    <TableHead className="text-right">Total Terbayarkan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentPlan.map((p, i) => {
                    const totalAfter = p.alreadyPaid + p.amountToPay;
                    const totalAfterPercent = p.contractValue > 0 ? Math.round(totalAfter / p.contractValue * 100) : 0;
                    return (
                      <TableRow key={p.projectId}>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{p.projectName}</span>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${
                                (p.financeBook || 'freelance') === 'company'
                                  ? 'bg-indigo-100 text-indigo-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}
                              data-testid={`plan-book-badge-${p.projectId}`}
                            >
                              {(p.financeBook || 'freelance') === 'company' ? 'Company' : 'Freelance'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(p.contractValue)}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatCurrency(p.alreadyPaid)}</span>
                          <span className="text-xs text-slate-400 ml-1">({p.alreadyPaidPercent}%)</span>
                        </TableCell>
                        <TableCell className="text-center bg-amber-50">
                          <span className={`font-bold ${p.clientPaidPercent >= 100 ? 'text-green-700' : 'text-blue-700'}`}>
                            {p.clientPaidPercent}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <input
                            type="number"
                            value={p.myPaymentPercent || ''}
                            onChange={(e) => updatePaymentPercent(i, e.target.value)}
                            className="w-16 h-8 text-center text-sm border rounded"
                            min="0" max="100"
                            placeholder="%"
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-700">
                          {p.amountToPay > 0 ? formatCurrency(p.amountToPay) : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-bold">{formatCurrency(totalAfter)}</span>
                          <span className="text-xs text-slate-400 ml-1">({totalAfterPercent}%)</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals */}
                  <TableRow className="bg-slate-100 font-bold border-t-2">
                    <TableCell colSpan={2} className="text-right">TOTAL</TableCell>
                    <TableCell className="text-right">{formatCurrency(paymentPlan.reduce((s, p) => s + p.contractValue, 0))}</TableCell>
                    <TableCell className="text-right">{formatCurrency(paymentPlan.reduce((s, p) => s + p.alreadyPaid, 0))}</TableCell>
                    <TableCell className="bg-amber-50"></TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-right text-green-700">{formatCurrency(paymentPlan.reduce((s, p) => s + p.amountToPay, 0))}</TableCell>
                    <TableCell className="text-right">{formatCurrency(paymentPlan.reduce((s, p) => s + p.alreadyPaid + p.amountToPay, 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Postponed Payments */}
        {postponedPayments.length > 0 && (
          <Card className="mb-6 border-amber-300">
            <CardHeader>
              <CardTitle className="text-amber-900">Postponed Payments ({postponedPayments.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {postponedPayments.map(pp => (
                  <div key={pp.id} className="p-4 bg-white border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{formatCurrency(pp.totalAmount)}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{new Date(pp.createdAt).toLocaleDateString('id-ID')}</span>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => executePostponedPayment(pp)} data-testid={`execute-pp-${pp.id}`}>
                          Execute
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deletePostponedPayment(pp.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {pp.projects?.map((p, i) => (
                        <div key={i} className="flex justify-between text-sm text-slate-600">
                          <span>{p.projectName}</span>
                          <span>{p.paymentPercent}% → {formatCurrency(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Certificate Payment Flow */}
        {certStep >= 1 && (
          <Card className="mb-6 border-slate-300">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{certStep === 1 ? '1. Select Projects for Certificate' : '2. Preview & Generate'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setCertStep(0)}>Close</Button>
              </div>
            </CardHeader>
            <CardContent>
              {certStep === 1 && (
                <div className="space-y-2">
                  {member.projects?.map(proj => {
                    const subWorks = getAssignedSubWorks(proj.projectId);
                    return (
                      <div key={proj.projectId} className="flex items-center gap-3 p-3 rounded hover:bg-slate-50 border">
                        <Checkbox checked={certSelectedProjects.includes(proj.projectId)} onCheckedChange={() => toggleCertProject(proj.projectId)} />
                        <div className="flex-1">
                          <span className="font-medium">{proj.projectName}</span>
                          <span className="text-sm text-slate-500 ml-2">{formatCurrency(proj.contractValue)}</span>
                          {subWorks.length > 0 && (
                            <div className="text-xs text-slate-400 mt-1">
                              Scope: {subWorks.map(sw => sw.name).join(', ')}
                            </div>
                          )}
                        </div>
                        <span className="text-sm text-green-600">{formatCurrency(proj.totalPaid)}</span>
                      </div>
                    );
                  })}
                  <Button onClick={generateCertPreview} className="mt-4" data-testid="cert-preview-btn">Generate Preview</Button>
                </div>
              )}

              {certStep === 2 && (
                <div>
                  <div className="flex justify-end mb-4">
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCertStep(1)}>Back</Button>
                      <Button onClick={generateCertPDF} className="bg-slate-800 hover:bg-slate-900" data-testid="cert-generate-btn">
                        <FileText className="w-4 h-4 mr-2" /> Generate PDF
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-6">
                    {certPreviewData.map(proj => (
                      <div key={proj.projectId} className="p-4 border rounded-lg">
                        <div className="flex justify-between mb-2">
                          <h3 className="font-bold text-slate-800">{proj.projectName}</h3>
                          <span className="font-medium">{formatCurrency(proj.contractValue)}</span>
                        </div>
                        <p className="text-xs text-slate-500 mb-1">Assignment: {proj.assignmentDate || '-'}</p>
                        {proj.subWorks.length > 0 && (
                          <div className="text-xs text-slate-600 mb-3 p-2 bg-slate-50 rounded">
                            <span className="font-medium">Scope:</span>
                            {proj.subWorks.map((sw, i) => <span key={i} className="ml-2">- {sw.name}</span>)}
                          </div>
                        )}
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Payment</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Amount</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {proj.transactions.length === 0 ? (
                              <TableRow><TableCell colSpan={3} className="text-center text-slate-400 text-sm">No payments recorded</TableCell></TableRow>
                            ) : proj.transactions.map((t, i) => (
                              <TableRow key={i}><TableCell>Payment {i + 1}</TableCell><TableCell>{t.date}</TableCell><TableCell className="text-right">{formatCurrency(t.amount)}</TableCell></TableRow>
                            ))}
                            <TableRow className="font-bold bg-slate-50">
                              <TableCell colSpan={2}>Total Paid</TableCell><TableCell className="text-right text-green-700">{formatCurrency(proj.totalPaid)}</TableCell>
                            </TableRow>
                            <TableRow className="font-bold">
                              <TableCell colSpan={2}>Remaining</TableCell><TableCell className="text-right text-orange-600">{formatCurrency((proj.contractValue || 0) - (proj.totalPaid || 0))}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    ))}
                    {/* Grand Total */}
                    <div className="p-4 bg-slate-100 rounded-lg border-2">
                      <h3 className="font-bold mb-2">Rekapitulasi</h3>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><span className="text-slate-600">Total Fee:</span><br/><span className="font-bold">{formatCurrency(certPreviewData.reduce((s, p) => s + (p.contractValue || 0), 0))}</span></div>
                        <div><span className="text-slate-600">Total Paid:</span><br/><span className="font-bold text-green-700">{formatCurrency(certPreviewData.reduce((s, p) => s + (p.totalPaid || 0), 0))}</span></div>
                        <div><span className="text-slate-600">Remaining:</span><br/><span className="font-bold text-orange-600">{formatCurrency(certPreviewData.reduce((s, p) => s + ((p.contractValue || 0) - (p.totalPaid || 0)), 0))}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Projects Table */}
        <Card className="bg-white shadow-sm">
          <CardHeader><CardTitle>Project Assignments</CardTitle></CardHeader>
          <CardContent>
            {member.projects?.length === 0 ? (
              <div className="text-center py-12"><p className="text-slate-600">No projects assigned yet</p></div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Project Name</TableHead><TableHead>Sub-Works</TableHead><TableHead>Assignment Date</TableHead>
                  <TableHead>Pending Tasks</TableHead><TableHead>Contract Value</TableHead><TableHead>Total Paid</TableHead>
                  <TableHead>Remaining</TableHead><TableHead>Contract</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {member.projects?.map((project, index) => {
                    const subWorks = getAssignedSubWorks(project.projectId);
                    return (
                      <TableRow key={index} className="cursor-pointer hover:bg-slate-50" onClick={() => openContractDetail(project)} data-testid={`project-row-${project.projectId}`}>
                        <TableCell className="font-medium text-blue-700 hover:underline">{project.projectName}</TableCell>
                        <TableCell className="text-xs text-slate-500">{subWorks.length > 0 ? subWorks.map(sw => sw.name).join(', ') : '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{project.assignmentDate || '-'}</TableCell>
                        <TableCell><Badge className="bg-orange-100 text-orange-800">{project.pendingTasks}</Badge></TableCell>
                        <TableCell>{formatCurrency(project.contractValue)}</TableCell>
                        <TableCell className="text-green-600 font-medium">{formatCurrency(project.totalPaid || 0)}</TableCell>
                        <TableCell className="text-orange-600 font-medium">{formatCurrency(project.unpaid)}</TableCell>
                        <TableCell>
                          {project.hasContractFile && (
                            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={() => viewContractFile(project.projectId, member.name)}><Eye className="w-4 h-4 text-blue-600" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => downloadContractFile(project.projectId, member.name)}><Download className="w-4 h-4 text-slate-600" /></Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Contract Detail Dialog */}
        <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Contract Detail - {selectedContract?.projectName}</DialogTitle></DialogHeader>
            {selectedContract && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xs text-slate-500">Project</p><p className="font-medium">{selectedContract.projectName}</p></div>
                  <div><p className="text-xs text-slate-500">Assignment Date</p><p className="font-medium">{selectedContract.assignmentDate || '-'}</p></div>
                  <div><p className="text-xs text-slate-500">Contract Value</p><p className="font-medium">{formatCurrency(selectedContract.contractValue)}</p></div>
                  <div><p className="text-xs text-slate-500">Total Paid</p><p className="font-medium text-green-600">{formatCurrency(selectedContract.totalPaid || 0)}</p></div>
                </div>
                {selectedContract.contractDescription && (
                  <div><p className="text-xs text-slate-500 mb-1">Work Description</p><p className="text-sm bg-slate-50 p-3 rounded border whitespace-pre-wrap">{selectedContract.contractDescription}</p></div>
                )}
                {(() => { const sws = getAssignedSubWorks(selectedContract.projectId); return sws.length > 0 ? (
                  <div><p className="text-xs text-slate-500 mb-1">Assigned Sub-Works</p>{sws.map((sw, i) => <div key={i} className="text-sm p-2 bg-slate-50 rounded mb-1 flex justify-between"><span>{sw.name}</span><span className="text-slate-400">{formatCurrency(sw.price)}</span></div>)}</div>
                ) : null; })()}
                {selectedContract.hasContractFile && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => viewContractFile(selectedContract.projectId, member.name)}><Eye className="w-4 h-4 mr-2" /> View</Button>
                    <Button variant="outline" size="sm" onClick={() => downloadContractFile(selectedContract.projectId, member.name)}><Download className="w-4 h-4 mr-2" /> Download</Button>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium mb-2">Payment History</p>
                  {transactions.filter(t => t.projectId === selectedContract.projectId).length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No payments recorded</p>
                  ) : transactions.filter(t => t.projectId === selectedContract.projectId).map((txn, i) => (
                    <div key={i} className="flex justify-between text-sm p-2 bg-slate-50 rounded mb-1"><span>{txn.name} ({txn.date})</span><span className="font-medium">{formatCurrency(txn.amount)}</span></div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeamDetail;
